import { StrictMode, lazy, Suspense } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { MantineProvider } from '@mantine/core'
import { ModalsProvider } from '@mantine/modals'
import { Notifications } from '@mantine/notifications'
import { HelmetProvider } from 'react-helmet-async'
import { ErrorBoundary } from 'react-error-boundary'
import type { ErrorInfo } from 'react'

import App from './App'
import { AuthProvider } from './contexts/AuthContext'
import { theme } from './theme'
import { MaintenanceErrorFallback } from './components/MaintenanceErrorFallback'

import '@mantine/core/styles.css'
import '@mantine/notifications/styles.css'
import './styles/global.css'
import { I18nProvider } from './contexts/I18nContext'
import { DarkModeProvider } from './contexts/DarkModeProvider'

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 1000 * 60 * 5, // 5 minutes
            gcTime: 1000 * 60 * 10, // 10 minutes
            retry: 1,
            refetchOnWindowFocus: false,
        },
    },
})

// Lazy load React Query DevTools only in development
const ReactQueryDevtoolsLazy = import.meta.env.DEV
    ? lazy(() => import('@tanstack/react-query-devtools').then(module => ({
        default: module.ReactQueryDevtools
    })))
    : null

function handleChunkError(error: Error, _info: ErrorInfo) {
    if (
        error &&
        error.message &&
        (
            /Loading chunk [\d]+ failed/i.test(error.message) ||
            /Cannot find module/i.test(error.message) ||
            /dynamic import/i.test(error.message) ||
            /ChunkLoadError/i.test(error.message)
        )
    ) {
        // Prevent infinite reload loops
        if (!(window as any).__chunk_reload_attempted) {
            (window as any).__chunk_reload_attempted = true;
            window.location.reload(); // No arguments needed
        }
    }
    // Optionally, log other errors here
}

createRoot(document.getElementById('root')!).render(
    <StrictMode>
        <HelmetProvider>
            <QueryClientProvider client={queryClient}>
                <BrowserRouter>
                    <DarkModeProvider>
                        <MantineProvider theme={theme}>
                            <ModalsProvider>
                                <I18nProvider>
                                    <AuthProvider>
                                        <ErrorBoundary FallbackComponent={MaintenanceErrorFallback} onError={handleChunkError}>
                                            <App />
                                            <Notifications position="top-right" />
                                        </ErrorBoundary>
                                    </AuthProvider>
                                </I18nProvider>
                            </ModalsProvider>
                        </MantineProvider>
                    </DarkModeProvider>
                </BrowserRouter>
                {ReactQueryDevtoolsLazy && (
                    <Suspense fallback={null}>
                        <ReactQueryDevtoolsLazy initialIsOpen={false} />
                    </Suspense>
                )}
            </QueryClientProvider>
        </HelmetProvider>
    </StrictMode>,
) 